import { useState, useEffect } from "react";
import { fetchEvents, fetchPhoto, savePhoto, deletePhoto, getCachedEvents, getCachedPhoto } from "./s3Events";
import { loadData, getCached } from "./s3Data";
import { AiOutlineCamera } from "react-icons/ai";
import { useIsMobile } from "../useIsMobile";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function toStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function localDate(dateStr) {
  return new Date(dateStr + "T12:00:00");
}

function fmt12(time) {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

const CAT_COLORS = {
  Appointment: "#E24B4A",
  Birthday:    "#D4537E",
  School:      "#378ADD",
  Work:        "#7F77DD",
  Family:      "#1D9E75",
  Holiday:     "#EF9F27",
  Reminder:    "#666",
};

const H = {
  root: {
    fontFamily: "'DM Mono', 'Courier New', monospace",
    background: "#0f0f11",
    minHeight: "100vh",
    color: "#e8e6e0",
    paddingTop: 70,
  },
  page: {
    padding: "32px 28px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  greeting: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 28,
    fontWeight: 600,
    color: "#f0ede6",
    letterSpacing: "-0.02em",
    marginBottom: 4,
  },
  subGreeting: {
    fontSize: 11,
    color: "#444",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 24,
  },
  card: {
    background: "#161618",
    border: "1px solid #2a2a2e",
    borderRadius: 12,
    padding: "24px 22px",
  },
  cardTitle: {
    fontSize: 10,
    color: "#555",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    marginBottom: 16,
  },
  photoEmptyArea: (height) => ({
    width: "100%",
    height,
    background: "#0f0f11",
    border: "2px dashed #2a2a2e",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    cursor: "pointer",
  }),
  photoLoadedArea: {
    width: "100%",
    position: "relative",
    borderRadius: 10,
    overflow: "hidden",
    background: "#0f0f11",
  },
  photoHint: {
    fontSize: 12,
    color: "#333",
    letterSpacing: "0.08em",
  },
  photoImg: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  removeBtn: {
    position: "absolute",
    top: 12,
    right: 12,
    background: "rgba(0,0,0,0.75)",
    border: "1px solid #444",
    borderRadius: 6,
    color: "#aaa",
    fontSize: 11,
    padding: "4px 10px",
    cursor: "pointer",
    letterSpacing: "0.06em",
    zIndex: 2,
  },
  uploadOverlay: {
    position: "absolute",
    bottom: 12,
    left: "50%",
    transform: "translateX(-50%)",
    background: "rgba(0,0,0,0.6)",
    border: "1px solid #333",
    borderRadius: 6,
    color: "#666",
    fontSize: 10,
    padding: "4px 12px",
    letterSpacing: "0.08em",
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  statusText: {
    fontSize: 10,
    color: "#555",
    textAlign: "center",
    marginTop: 8,
    letterSpacing: "0.06em",
  },
  eventCard: (color) => ({
    background: "#111113",
    border: "1px solid #1e1e22",
    borderLeft: `3px solid ${color}`,
    borderRadius: 8,
    padding: "14px 16px",
  }),
  eventDate: {
    fontSize: 10,
    color: "#444",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 6,
  },
  eventTitle: {
    fontSize: 14,
    color: "#e8e6e0",
    fontWeight: 500,
    marginBottom: 4,
    lineHeight: 1.4,
  },
  eventTime: {
    fontSize: 11,
    color: "#555",
  },
  empty: {
    fontSize: 12,
    color: "#333",
    textAlign: "center",
    padding: "20px 0",
  },
};

function getUpcoming(evList) {
  const today = toStr(new Date());
  return (evList || [])
    .filter(e => e.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date) || (a.time || "").localeCompare(b.time || ""))
    .slice(0, 3);
}

export default function Home() {
  const isMobile = useIsMobile();
  const [events, setEvents] = useState(() => getUpcoming(getCachedEvents()));
  const [photoSrc, setPhotoSrc] = useState(() => getCachedPhoto());
  const [photoStatus, setPhotoStatus] = useState(() => getCachedPhoto() ? "idle" : "loading");
  const [tonightDinner, setTonightDinner] = useState(() => {
    const todayName = DAY_NAMES[new Date().getDay()];
    const cached = getCached("meals.json", null);
    return cached ? (cached[todayName]?.Dinner ?? "") : null;
  });

  useEffect(() => {
    fetchEvents().then(data => {
      if (data !== null) setEvents(getUpcoming(data));
    }).catch(() => {});

    fetchPhoto().then(url => {
      if (url) setPhotoSrc(url);
      setPhotoStatus("idle");
    }).catch(() => setPhotoStatus("idle"));

    const todayName = DAY_NAMES[new Date().getDay()];
    loadData("meals.json", null).then(meals => {
      if (meals !== null) setTonightDinner(meals?.[todayName]?.Dinner ?? "");
    }).catch(() => {});
  }, []);

  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1800;
        let w = img.naturalWidth, h = img.naturalHeight;
        if (w > MAX || h > MAX) {
          const ratio = Math.min(MAX / w, MAX / h);
          w = Math.floor(w * ratio);
          h = Math.floor(h * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.88);
        setPhotoSrc(dataUrl);
        setPhotoStatus("saving");
        savePhoto(dataUrl)
          .then(() => setPhotoStatus("saved"))
          .catch(() => setPhotoStatus("error"));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  }

  async function removePhoto(e) {
    e.preventDefault();
    e.stopPropagation();
    setPhotoSrc(null);
    setPhotoStatus("saving");
    try {
      await deletePhoto();
      setPhotoStatus("idle");
    } catch {
      setPhotoStatus("error");
    }
  }

  const today = new Date();
  const dateLabel = today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const photoEmptyHeight = isMobile ? 180 : 260;

  return (
    <div style={H.root}>
      <div style={{ ...H.page, padding: isMobile ? "20px 12px" : H.page.padding }}>
        <p style={H.greeting}>Campbell Family</p>
        <p style={H.subGreeting}>{dateLabel}</p>

        {/* Full-width photo hero */}
        <div style={{ ...H.card, padding: "20px 20px", marginBottom: 16 }}>
          <p style={H.cardTitle}>Family Photo</p>
          <label style={{ display: "block", cursor: "pointer" }}>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handlePhotoChange}
            />
            {photoStatus === "loading" ? (
              <div style={H.photoEmptyArea(photoEmptyHeight)}>
                <span style={H.photoHint}>Loading…</span>
              </div>
            ) : photoSrc ? (
              <div style={H.photoLoadedArea}>
                <img src={photoSrc} alt="Family" style={H.photoImg} />
                <button style={H.removeBtn} onClick={removePhoto}>remove</button>
                <span style={H.uploadOverlay}>click to change photo</span>
              </div>
            ) : (
              <div style={H.photoEmptyArea(photoEmptyHeight)}>
                <AiOutlineCamera style={{ fontSize: 44, color: "#2a2a2e" }} />
                <span style={H.photoHint}>Click to add a family photo</span>
              </div>
            )}
          </label>
          {photoStatus === "saving" && <p style={H.statusText}>Saving…</p>}
          {photoStatus === "saved" && <p style={{ ...H.statusText, color: "#1D9E75" }}>Saved</p>}
          {photoStatus === "error" && <p style={{ ...H.statusText, color: "#E24B4A" }}>Save failed</p>}
        </div>

        {/* Upcoming events — 3-column strip */}
        {/* Tonight's Dinner */}
        <div style={{ ...H.card, padding: "20px 22px", marginBottom: 16 }}>
          <p style={H.cardTitle}>Tonight's Dinner</p>
          {tonightDinner === null ? (
            <p style={{ fontSize: 13, color: "#333" }}>Loading…</p>
          ) : tonightDinner ? (
            <p style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 22, fontWeight: 600, color: "#f0ede6", letterSpacing: "-0.01em", margin: 0 }}>
              {tonightDinner}
            </p>
          ) : (
            <p style={{ fontSize: 13, color: "#333", margin: 0 }}>
              Nothing planned — add it in <span style={{ color: "#555" }}>Meals</span>
            </p>
          )}
        </div>

        <div style={{ ...H.card, padding: "20px 20px" }}>
          <p style={H.cardTitle}>Upcoming Events</p>
          {events.length === 0 ? (
            <p style={H.empty}>No upcoming events — add them in the Calendar</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : `repeat(${events.length}, 1fr)`, gap: 12 }}>
              {events.map(ev => {
                const d = localDate(ev.date);
                const label = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
                const color = CAT_COLORS[ev.category] || "#555";
                return (
                  <div key={ev.id} style={H.eventCard(color)}>
                    <p style={H.eventDate}>{label}</p>
                    <p style={H.eventTitle}>{ev.title}</p>
                    <p style={H.eventTime}>{ev.allDay ? ev.category : `${fmt12(ev.time)} · ${ev.category}`}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
